import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Radar, RefreshCw, Sun, Moon, LayoutGrid, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSiteShift } from "@/hooks/useSiteShift";
import { can } from "@/lib/permissions";
import ShiftCrewCard from "@/components/ShiftCrewCard";
import InspectionDetail from "@/components/InspectionDetail";

/**
 * Superintendent Command Centre — one screen, both shifts, no clicks wasted.
 *
 * A supervisor / superintendent lands here and sees Day + Night side-by-side
 * for the current date: active crew, live progress, roll-ups, and every
 * inspection on the swing. Click any inspection and the standard inspection
 * drawer opens in-place — no route hop, no context loss.
 *
 * Data sources (all read-only from the prod API):
 *   • GET /swings         — group by shift, filter by date
 *   • GET /shift-summary  — fetched twice (day + night) for roll-ups
 *
 * Pattern:
 *   • Poll every 15s (matches useActiveSwing) so the two columns stay live.
 *   • Pause polling while the inspection drawer is open (no clobbering
 *     optimistic edits on the drawer).
 *   • URL-driven date + view mode so supervisors can bookmark
 *     `?date=2026-07-07&view=day` for a shift they care about.
 */
const POLL_MS = 8000;

export default function SuperintendentCentre() {
  const { user } = useAuth();
  const { shift: liveShift, ready: shiftReady } = useSiteShift();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialDate = searchParams.get("date") || todayIso();
  // Default view — if the URL hasn't been forced by the user, auto-focus
  // whichever shift is LIVE RIGHT NOW (day vs night per site-config windows).
  // Josh's complaint was that when they were on nights, the portal defaulted
  // to a Day view. Now the live shift is what greets them.
  const urlView = searchParams.get("view");
  const initialView = urlView || (liveShift === "night" ? "night" : liveShift === "day" ? "day" : "both");

  const [date, setDate] = useState(initialDate);
  const [view, setView] = useState(initialView);
  const [swings, setSwings] = useState([]);
  const [daySummary, setDaySummary] = useState(null);
  const [nightSummary, setNightSummary] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [customJobs, setCustomJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openInspId, setOpenInspId] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  // Auto-follow the live shift until the supervisor explicitly clicks a
  // view chip THIS SESSION. Josh reported (Jul 9) that B Crew was on Nights
  // but the Command Centre was stuck on Days: root cause was
  // `viewTouched = !!urlView`, combined with the URL-sync effect below
  // stamping `?view=…` on every render. On the *next* page load, `urlView`
  // was always truthy → `viewTouched=true` → auto-sync skipped → the view
  // stayed frozen on whatever shift was auto-picked hours earlier, long
  // after the shift boundary had rolled over.
  //
  // Fix: `viewTouched` now starts `false` on every mount. A stale
  // `?view=day` in the URL is honoured as the *initial* hint, but the
  // useEffect below is allowed to re-align the view the moment
  // useSiteShift resolves a fresh live shift. Clicking a chip flips
  // `viewTouched` to `true` so the supervisor's manual pick is preserved
  // for the rest of the session (until reload).
  const [viewTouched, setViewTouched] = useState(false);
  useEffect(() => {
    if (viewTouched) return;
    // Only auto-align once the site-config windows have actually loaded —
    // otherwise a fresh mount at 22:30 would briefly pin the view to "day"
    // from the browser-local 06→18 fallback in useSiteShift before the
    // real Australia/Sydney night window classified it correctly.
    if (!shiftReady) return;
    if (liveShift === "night" && view !== "night") setView("night");
    else if (liveShift === "day" && view !== "day") setView("day");
  }, [liveShift, shiftReady, viewTouched, view]);

  // Keep the URL in sync so supervisors can share a link to a specific date.
  useEffect(() => {
    const p = new URLSearchParams(searchParams);
    p.set("date", date);
    p.set("view", view);
    setSearchParams(p, { replace: true });
  }, [date, view, searchParams, setSearchParams]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [swingsRes, dayRes, nightRes, inspRes, cjRes] = await Promise.all([
        api.get("/swings", { params: { limit: 100 } }),
        api.get("/shift-summary", { params: { date, shift: "day" } }),
        api.get("/shift-summary", { params: { date, shift: "night" } }),
        // Fetch every inspection for the site so we can enrich the swing
        // roster with technician + live progress (same data Supervisors see).
        api.get("/inspections", { params: { limit: 200 } }),
        // Custom jobs (asset-demands) — signed off during either shift on the
        // selected date show up under the correct column.
        api.get("/asset-demands", { params: { limit: 100 } }).catch(() => ({ data: [] })),
      ]);
      const listOf = (r) => {
        const raw = r?.data;
        if (Array.isArray(raw)) return raw;
        return raw?.items || raw?.data || [];
      };
      setSwings(listOf(swingsRes).filter((s) => s.start_date === date));
      setDaySummary(dayRes.data);
      setNightSummary(nightRes.data);
      setInspections(listOf(inspRes));
      setCustomJobs(listOf(cjRes));
    } catch { /* silent — next poll retries */ }
    finally { setLoading(false); }
  }, [date]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll, refreshTick]);

  // Live polling every 15s — pause when the inspection drawer is open so
  // it never interrupts the supervisor mid-sign-off.
  useEffect(() => {
    if (openInspId) return undefined;
    const iv = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "visible") {
        fetchAll();
      }
    }, POLL_MS);
    return () => clearInterval(iv);
  }, [fetchAll, openInspId]);

  // Pick the active swing per shift (prefer status=active over closed).
  const daySwing = useMemo(() => pickSwingForShift(swings, "day"), [swings]);
  const nightSwing = useMemo(() => pickSwingForShift(swings, "night"), [swings]);

  // Enrich each swing with its actual inspection records so the ShiftCrewCard
  // can show technician + live progress instead of just an id list.
  const enrichSwing = useCallback((swing) => {
    if (!swing) return null;
    const idSet = new Set((swing.inspection_ids || []).map(String));
    const rows = inspections
      .filter((i) => idSet.has(String(i.id || i._id)))
      .map((i) => {
        const p = i.progress || {};
        const total = Number(p.total || (i.sections || []).reduce((n, s) => n + (s.items?.length || 0), 0));
        const done = Number(p.approved || 0) + Number(p.ready || 0) + Number(p.in_progress || 0);
        const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
        return {
          id: i.id || i._id,
          asset_id: i.asset_id,
          template_name: i.template_name || i.title || "Inspection",
          technician: i.started_by_name || i.inspector_name || i.created_by_name || "—",
          status: i.status || "planned",
          pct,
          total, done,
          started_at: i.started_at,
        };
      })
      .sort((a, b) => b.pct - a.pct || String(a.asset_id).localeCompare(String(b.asset_id)));
    return { ...swing, __inspectionRows: rows };
  }, [inspections]);

  const dayEnriched = useMemo(() => enrichSwing(daySwing), [daySwing, enrichSwing]);
  const nightEnriched = useMemo(() => enrichSwing(nightSwing), [nightSwing, enrichSwing]);

  // Custom jobs for the shift — asset-demands opened/closed within the day.
  const dayJobs = useMemo(() => filterJobsForShift(customJobs, date, "day"), [customJobs, date]);
  const nightJobs = useMemo(() => filterJobsForShift(customJobs, date, "night"), [customJobs, date]);

  const shiftDelta = (n) => {
    const d = new Date(date + "T00:00:00");
    d.setDate(d.getDate() + n);
    setDate(d.toISOString().slice(0, 10));
  };

  // Which crew is LIVE right now, for the header banner.
  const liveSwing = liveShift === "night" ? nightSwing : (liveShift === "day" ? daySwing : null);
  const wk = weekOfYear(new Date(date + "T00:00:00"));

  if (!can(user, "crewDashboard")) {
    return (
      <div className="panel-flat p-8 text-center" data-testid="command-centre-forbidden">
        <div className="font-display text-2xl font-black mb-2">Command Centre</div>
        <div className="text-muted">This dashboard is for Supervisors and above.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="command-centre-page">
      {/* Header */}
      <div className="panel-flat p-4 md:p-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3 shrink-0">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, rgba(245,130,32,0.15) 0%, rgba(245,130,32,0.03) 100%)",
              border: "1px solid rgba(245,130,32,0.35)",
            }}
          >
            <Radar className="w-5 h-5" style={{ color: "var(--primary)" }} />
          </div>
          <div>
            <div className="font-display text-2xl font-black leading-none uppercase tracking-tight" data-testid="command-centre-title">
              Command Centre
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted mt-1 font-bold">
              Day + Night · Live Crew View · {user?.site_code || "Site"}
            </div>
          </div>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Date stepper */}
          <div className="inline-flex items-center rounded-xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
            <button
              className="btn btn-sm btn-ghost !rounded-none"
              onClick={() => shiftDelta(-1)}
              title="Previous day"
              data-testid="date-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-transparent px-3 py-1.5 text-sm font-black tabular-nums outline-none"
              data-testid="date-picker"
              style={{ colorScheme: "dark", minWidth: 140 }}
            />
            <button
              className="btn btn-sm btn-ghost !rounded-none"
              onClick={() => shiftDelta(1)}
              title="Next day"
              data-testid="date-next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              className="btn btn-sm btn-ghost !rounded-none !border-l"
              style={{ borderLeftColor: "rgba(255,255,255,0.1)" }}
              onClick={() => setDate(todayIso())}
              title="Jump to today"
              data-testid="date-today"
            >
              Today
            </button>
          </div>

          {/* View toggle — a "● LIVE" dot on Day/Night matches whichever
              shift is currently active per site-config, so the supervisor
              can see at a glance which column reflects the actual live
              shift. Clicking any chip pins the view for this session
              (viewTouched → auto-sync stops). Reloading re-enables
              auto-follow. */}
          <div className="inline-flex items-center rounded-xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
            <ViewButton icon={LayoutGrid} label="Both" active={view === "both"} onClick={() => { setView("both"); setViewTouched(true); }} testid="view-both" />
            <ViewButton icon={Sun} label="Day" active={view === "day"} live={shiftReady && liveShift === "day"} onClick={() => { setView("day"); setViewTouched(true); }} testid="view-day" />
            <ViewButton icon={Moon} label="Night" active={view === "night"} live={shiftReady && liveShift === "night"} onClick={() => { setView("night"); setViewTouched(true); }} testid="view-night" />
          </div>
          {viewTouched && (
            <button
              type="button"
              onClick={() => setViewTouched(false)}
              className="pill !text-[10px] !py-1 !px-2 uppercase font-black tracking-widest hover:opacity-80"
              style={{
                background: "rgba(74,222,128,0.14)",
                color: "#4ade80",
                border: "1px solid rgba(74,222,128,0.4)",
              }}
              title="Auto-follow the live shift again"
              data-testid="view-follow-live"
            >
              ● Follow live
            </button>
          )}

          <button
            className="btn btn-sm btn-ghost"
            onClick={() => setRefreshTick((t) => t + 1)}
            title="Refresh"
            data-testid="btn-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* LIVE NOW banner — auto-detected shift + week + active crew.
          Josh reported the portal was showing "Day" while they were on
          nights; this strip removes that ambiguity. Turns green when a
          crew is actually on shift, amber when we detect the shift but no
          swing has been started, grey when we can't classify. Gated on
          `shiftReady` so we don't flash a stale fallback classification
          (which used a browser-local 06:00→18:00 heuristic) before the
          real site-config windows arrive from the server. */}
      {liveShift && shiftReady && (
        <div
          className="panel-flat px-4 py-3 flex items-center gap-3 flex-wrap"
          data-testid="command-centre-live-strip"
          style={{
            borderLeft: `3px solid ${liveShift === "night" ? "#7aa2ff" : "#f5b942"}`,
            background: liveShift === "night" ? "rgba(122,162,255,0.06)" : "rgba(245,185,66,0.06)",
          }}
        >
          <div
            className="pill !text-[10px] !py-1 !px-2 uppercase font-black tracking-widest"
            style={{
              background: liveSwing ? "rgba(74,222,128,0.14)" : "rgba(148,163,184,0.14)",
              color: liveSwing ? "#4ade80" : "#94a3b8",
              border: `1px solid ${liveSwing ? "rgba(74,222,128,0.4)" : "rgba(148,163,184,0.3)"}`,
            }}
          >
            {liveSwing ? "● LIVE NOW" : "● OFF-SHIFT"}
          </div>
          <div className="text-sm">
            <span className="font-black uppercase tracking-widest text-white" data-testid="live-shift-label">
              {liveShift === "night" ? "NIGHTS" : "DAYS"}
            </span>
            {liveSwing?.crew_name && (
              <>
                <span className="text-muted mx-2">·</span>
                <span className="font-black text-white" data-testid="live-crew-label">{liveSwing.crew_name}</span>
              </>
            )}
            <span className="text-muted mx-2">·</span>
            <span className="text-white" data-testid="live-week-label">Week {wk}</span>
            {liveSwing?.supervisor_name && (
              <>
                <span className="text-muted mx-2">·</span>
                <span className="text-white/85">Supervisor {liveSwing.supervisor_name}</span>
              </>
            )}
          </div>
          <div className="ml-auto text-[10px] uppercase tracking-widest text-muted">
            Auto-detected · Site {user?.site_code || "—"}
          </div>
        </div>
      )}

      {/* Two-column grid — collapses to single column on mobile */}
      <div
        className={`grid gap-4 md:gap-6 ${
          view === "both" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"
        }`}
        data-testid="command-centre-grid"
      >
        {(view === "both" || view === "day") && (
          <ShiftCrewCard
            shift="day"
            date={date}
            swing={dayEnriched}
            summary={daySummary}
            customJobs={dayJobs}
            onOpenInsp={setOpenInspId}
            loading={loading}
          />
        )}
        {(view === "both" || view === "night") && (
          <ShiftCrewCard
            shift="night"
            date={date}
            swing={nightEnriched}
            summary={nightSummary}
            customJobs={nightJobs}
            onOpenInsp={setOpenInspId}
            loading={loading}
          />
        )}
      </div>

      {/* Inspection drawer — opens InspectionDetail's own full-screen
          overlay so supervisors can approve without leaving the two-column
          command centre. Closing kicks a refresh so both shift cards catch
          up to whatever the supervisor just signed/edited. */}
      {openInspId && (
        <InspectionDetail
          inspectionId={openInspId}
          onClose={() => {
            setOpenInspId(null);
            setRefreshTick((t) => t + 1);
          }}
          onUpdated={() => setRefreshTick((t) => t + 1)}
        />
      )}
    </div>
  );
}

function ViewButton({ icon: Icon, label, active, live, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-black uppercase tracking-widest transition-colors"
      style={{
        background: active ? "var(--primary)" : "transparent",
        color: active ? "#0a0a0a" : "rgba(255,255,255,0.6)",
      }}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">{label}</span>
      {live && (
        <span
          className="absolute -top-1 -right-1 w-2 h-2 rounded-full animate-pulse"
          style={{
            background: "#4ade80",
            boxShadow: "0 0 8px #4ade80, 0 0 2px #4ade80",
            border: "1px solid rgba(0,0,0,0.55)",
          }}
          aria-label="Live shift"
          data-testid={`${testid}-live-dot`}
        />
      )}
    </button>
  );
}

function todayIso() {
  const d = new Date();
  // Local YYYY-MM-DD — not UTC, so the shift boundary is the miner's clock.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function filterJobsForShift(jobs, date, shift) {
  // Match asset-demands that were created OR closed on the target date and
  // (roughly) belong to the requested shift. Server doesn't stamp shift on
  // asset-demands, so we treat "created before 12:00 local" as day and after
  // as night — good enough for the roll-up count on the card.
  if (!Array.isArray(jobs) || !jobs.length) return [];
  return jobs.filter((j) => {
    const created = j.created_at || j.closed_at || j.updated_at;
    if (!created) return false;
    try {
      const d = new Date(created);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (iso !== date) return false;
      const isDay = d.getHours() >= 5 && d.getHours() < 17;
      return shift === "day" ? isDay : !isDay;
    } catch { return false; }
  });
}

function weekOfYear(date = new Date()) {
  // ISO week number — Thursday-anchor. July 8, 2026 → 28.
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function pickSwingForShift(swings, shift) {
  // The API doesn't yet expose a `.shift` column, but the swing_label always
  // encodes it. We match a case-insensitive word-boundary `\bday\b` or
  // `\bnight\b` anywhere in the label so typos (`Nights`, non-breaking
  // spaces, missing bullets, "Day Shift", etc.) don't silently drop the swing
  // off its card. Prefer an active swing over a closed one so the live-crew
  // card is always the loudest signal on screen.
  const testers = {
    day: /\bday\b/i,
    night: /\bnight\b/i,
  };
  const rx = testers[shift];
  if (!rx) return null;
  const candidates = swings.filter((s) => {
    const label = String(s.swing_label || s.shift || "");
    if (!rx.test(label)) {
      // One-shot console warning so an unclassifiable swing is discoverable
      // in a browser inspection but doesn't spam the console.
      if (!s.__shift_warned__) {
        console.warn("[Command Centre] Swing not classifiable to day/night:", label, s.id);
        // Mark on the object (non-enumerable-ish) so we don't loop-log.
        try { s.__shift_warned__ = true; } catch { /* frozen — ignore */ }
      }
      return false;
    }
    return true;
  });
  const active = candidates.find((s) => s.status === "active");
  return active || candidates[0] || null;
}
